import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { HiringPlanRequestViewModal } from "@/pages/recruit/hiring-plan/widgets/HiringPlanRequestViewModal.tsx";
import { useApprovalRequestCancel } from "@/queries/recruit/use-hiring-plan.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiPaginationMetadata } from "@skailer-com/uikit/core";
import {
  ApiHiringPlanRequest,
  getHiringPlanRequestStatusColor,
  getHiringPlanRequestStatusTitle,
} from "@/types/recruit/hiring-plan.types.ts";
import {
  ConfirmationModal,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  LinkTo,
  PaginationBlock,
  Stack,
  Table,
  Tag,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface PositionRequestsTableProps {
  items: ApiHiringPlanRequest[];
  isLoading: boolean;
  isFetching: boolean;
  onPageChange: (page: number) => void;
  isFiltering: boolean;
  metadata?: ApiPaginationMetadata;
  stickyLayout?: boolean;
  defaultVisibleRecords?: number;
}

export function HiringPlanTable({
  items,
  isLoading,
  isFetching,
  onPageChange,
  isFiltering = false,
  metadata,
  stickyLayout = false,
  defaultVisibleRecords,
}: PositionRequestsTableProps) {
  const { t } = useTranslation();
  const { renderDate } = useFormatter();

  const { openModal } = useModals();
  const requestCancel = useApprovalRequestCancel();
  const [showAll, setShowAll] = useState(false);

  const displayItems = useMemo(() => {
    if (defaultVisibleRecords == null || showAll || items.length <= defaultVisibleRecords) {
      return items;
    }

    return items.slice(0, defaultVisibleRecords);
  }, [defaultVisibleRecords, items, showAll]);

  const remainingRecords =
    defaultVisibleRecords == null ? 0 : Math.max(0, items.length - defaultVisibleRecords);

  const handleCancel = useCallback(
    async (id: number) => {
      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("approval:cancel_request_confirm_description"),
        acceptTitle: t("common:yes"),
        onAccept: async function () {
          try {
            await requestCancel.mutateAsync(id);

            analyticsService.trackEvent(analyticEvents.hiringPlan.requestAction, {
              [analyticProperties.id]: id,
              [analyticProperties.title]: "Position Request",
              [analyticProperties.actionType]: "Cancel",
            });
          } catch {
            return;
          }
        },
      });
    },
    [openModal, requestCancel, t]
  );

  const columns = useMemo<ColumnDef<ApiHiringPlanRequest>[]>(
    () => [
      {
        id: "created_at",
        header: t("common:created"),
        cell: (cell) => (
          <Text>{renderDate(cell.row.original.hiring_plan_data.datetime_created_at)}</Text>
        ),
        ...ColumnSizes.status,
      },

      {
        header: t("hiring_plan:position"),
        cell: (row) => (
          <Text className={"font-semibold text-default"}>
            {row.row.original.hiring_plan_data.title}
          </Text>
        ),
        accessorKey: "title",
        ...ColumnSizes.wide,
      },
      {
        header: t("common:status"),
        cell: (row) => (
          <Stack items={"start"}>
            <Tag
              variant={
                getHiringPlanRequestStatusColor(row.row.original.hiring_plan_data.status).tagVariant
              }
            >
              {getHiringPlanRequestStatusTitle(row.row.original.hiring_plan_data.status)}
            </Tag>
          </Stack>
        ),
        accessorKey: "status",
        ...ColumnSizes.status,
      },
      {
        header: t("ats:job"),
        cell: (row) => (
          <div className={"overflow-hidden text-ellipsis"}>
            <LinkTo
              className={"no-underline hover:underline underline-offset-2"}
              target={"_blank"}
              to={`/recruitment/candidates?job_id=${row.row.original.hiring_plan_data.job?.id}`}
            >
              {row.row.original.hiring_plan_data.job?.title ?? "-"}
            </LinkTo>
          </div>
        ),
        accessorKey: "job",
        ...ColumnSizes.standard,
        meta: { disableRowClick: true },
      },
      {
        header: t("core:department"),
        cell: (row) => (
          <div className={"overflow-hidden text-ellipsis"}>
            {row.row.original.hiring_plan_data.department?.title ?? "-"}
          </div>
        ),
        accessorKey: "department",
        ...ColumnSizes.standard,
      },
      {
        header: t("common:location"),
        cell: (row) => <div>{row.row.original.hiring_plan_data.location?.title ?? "-"}</div>,
        accessorKey: "location",
        ...ColumnSizes.standard,
      },
      {
        header: t("core:division"),
        cell: (row) => <div>{row.row.original.hiring_plan_data.division?.title ?? "-"}</div>,
        accessorKey: "division",
        ...ColumnSizes.standard,
      },
      {
        header: t("hiring_plan:plan_date"),
        cell: (row) => (
          <div>
            {row.row.original.hiring_plan_data.date_plan
              ? renderDate(row.row.original.hiring_plan_data.date_plan)
              : "-"}
          </div>
        ),
        accessorKey: "date",
        ...ColumnSizes.date,
      },
      {
        header: t("common:created_by"),
        cell: (row) => (
          <div>
            {row.row.original.approval_request_details &&
              row.row.original.approval_request_details.created_by && (
                <EmployeeHoverCard
                  employee={row.row.original.approval_request_details.created_by}
                  position={getEmployeePositionTitle(
                    row.row.original.approval_request_details.created_by
                  )}
                >
                  <EmployeeItem
                    employee={row.row.original.approval_request_details.created_by}
                    showPosition={false}
                    size={"sm"}
                    enableLink={true}
                    target={"_blank"}
                  />
                </EmployeeHoverCard>
              )}
          </div>
        ),
        accessorKey: "created",
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
      },
    ],
    []
  );

  const renderRowAction = useCallback(
    (row: Row<ApiHiringPlanRequest>) => {
      const request = row.original;
      const appovalRequestDetails = request.approval_request_details;
      if (!appovalRequestDetails) {
        return null;
      }
      if (!request.acl?.can_view && !request.acl?.can_cancel) {
        return null;
      }

      return (
        <DropdownMenu>
          {request.acl.can_view && (
            <DropdownMenuItem
              onClick={() => {
                openModal(HiringPlanRequestViewModal, {
                  requestId: appovalRequestDetails.id,
                });
              }}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-eye"} />}
              title={t("common:view")}
            />
          )}
          {request.acl.can_cancel && (
            <DropdownMenuItem
              onClick={async () => {
                await handleCancel(appovalRequestDetails.id);
              }}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-xmark"} />}
              title={t("common:cancel") + "..."}
            />
          )}
        </DropdownMenu>
      );
    },
    [handleCancel, openModal, t]
  );

  return (
    <Stack>
      <Table
        stickyHeader={stickyLayout}
        renderRowAction={renderRowAction}
        onRowClick={(row) => {
          if (row.original.acl.can_view && row.original.approval_request_details) {
            openModal(HiringPlanRequestViewModal, {
              requestId: row.original.approval_request_details.id,
            });
          }
        }}
        data={displayItems}
        columns={columns}
        showSkeleton={isLoading}
        isFetching={isFetching}
        showNotFoundError={isFiltering && metadata?.total_items == 0}
        notFoundComponent={<EmptyStateNothingFound />}
        emptyStateComponent={<EmptyState />}
      />
      {!showAll && remainingRecords > 0 && (
        <Stack direction="horizontal" className={"pt-(--gap-md)"}>
          <Button
            onClick={() => setShowAll(true)}
            variant="secondary"
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
          >
            {t("common:show_more_template", { count: remainingRecords })}
          </Button>
        </Stack>
      )}
      {!stickyLayout && (defaultVisibleRecords == null || showAll) && (
        <PaginationBlock metadata={metadata} onPageChange={onPageChange} />
      )}
    </Stack>
  );
}
